import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { GraduationCap, Mail, Lock, ArrowLeft, Presentation, KeyRound } from "lucide-react";

const getRedirectPath = (role) => {
  if (role === "admin") return "/admin";
  if (role === "teacher") return "/teacher";
  return "/student";
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const expectedRole = searchParams.get("role");
  const redirectTo = searchParams.get("redirect");
  const { login, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState("password"); // "password" or "pin"
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [pinDigits, setPinDigits] = useState(["", "", "", ""]);
  const pinRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    if (user) navigate(redirectTo || getRedirectPath(user.role));
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { access_token, user: userData } = response.data;
      if (expectedRole) {
        if (expectedRole === "student" && userData.role !== "student") {
          toast.error("This login is for students only. Please use the Teacher login.");
          setLoading(false);
          return;
        }
        if (expectedRole === "teacher" && userData.role !== "teacher" && userData.role !== "admin") {
          toast.error("This login is for teachers only. Please use the Student login.");
          setLoading(false);
          return;
        }
      }
      login(userData, access_token);
      toast.success("Welcome back!");
      navigate(redirectTo || getRedirectPath(userData.role));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newDigits = [...pinDigits];
    newDigits[index] = value;
    setPinDigits(newDigits);
    if (value && index < 3) {
      pinRefs[index + 1].current?.focus();
    }
    // Auto-submit when all 4 digits entered
    if (value && index === 3 && newDigits.every(d => d)) {
      submitPin(newDigits.join(""));
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted.length === 4) {
      const digits = pasted.split("");
      setPinDigits(digits);
      pinRefs[3].current?.focus();
      submitPin(pasted);
    }
  };

  const submitPin = async (pin) => {
    if (pin.length !== 4) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login-pin`, { pin });
      const { access_token, user: userData } = response.data;
      login(userData, access_token);
      toast.success("Welcome back!");
      navigate(redirectTo || getRedirectPath(userData.role));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid PIN");
      setPinDigits(["", "", "", ""]);
      pinRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const getLoginTitle = () => {
    if (expectedRole === "student") return "Student Login";
    if (expectedRole === "teacher") return "Teacher Login";
    return "Welcome Back";
  };

  const getLoginDescription = () => {
    if (expectedRole === "student") return "Sign in with your student credentials";
    if (expectedRole === "teacher") return "Sign in with your teacher credentials";
    return "Sign in to your TheMatheMind 360 account";
  };

  const getLoginIcon = () => {
    if (expectedRole === "teacher") return <Presentation className="w-7 h-7 text-primary-foreground" />;
    return <img src="/logo.png" alt="TheMatheMind 360" className="h-8 rounded" />;
  };

  const isStudentLogin = expectedRole === "student";
  const showGoogle = expectedRole !== "teacher";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          data-testid="back-to-home-btn"
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-8 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Button>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4">
              {getLoginIcon()}
            </div>
            <CardTitle className="text-2xl font-bold font-['Manrope']">{getLoginTitle()}</CardTitle>
            <CardDescription>{getLoginDescription()}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {expectedRole && (
              <div className="flex justify-center">
                <span className={`px-3 py-1 text-sm rounded-full ${
                  expectedRole === "student" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                }`}>
                  {expectedRole === "student" ? "Student Portal" : "Teacher Portal"}
                </span>
              </div>
            )}

            {/* PIN / Password toggle (students + generic sign-in) */}
            {showGoogle && (
              <div className="flex rounded-full bg-muted p-1 gap-1" data-testid="login-mode-toggle">
                <button
                  type="button"
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                    loginMode === "password" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setLoginMode("password")}
                  data-testid="mode-password-btn"
                >
                  <Lock className="w-3.5 h-3.5" />
                  Password
                </button>
                <button
                  type="button"
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                    loginMode === "pin" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => { setLoginMode("pin"); setTimeout(() => pinRefs[0].current?.focus(), 100); }}
                  data-testid="mode-pin-btn"
                >
                  <KeyRound className="w-3.5 h-3.5" />
                  PIN
                </button>
              </div>
            )}

            {showGoogle && (
              <>
                <Button
                  data-testid="google-signin-btn"
                  type="button"
                  variant="outline"
                  className="w-full h-11 rounded-full font-medium border-border hover:bg-muted"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    if (redirectTo) sessionStorage.setItem("post_login_redirect", redirectTo);
                    const redirectUrl = window.location.origin + "/student";
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                  </svg>
                  Continue with Google
                </Button>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">or</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              </>
            )}

            {/* Password Login Form */}
            {loginMode === "password" ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="email-input"
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      className="pl-10 h-11 rounded-lg"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="password-input"
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10 h-11 rounded-lg"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-medium"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            ) : (
              /* PIN Login Form */
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">Enter your 4-digit PIN</p>
                  <div className="flex justify-center gap-3" onPaste={handlePinPaste}>
                    {pinDigits.map((digit, i) => (
                      <input
                        key={i}
                        ref={pinRefs[i]}
                        data-testid={`pin-digit-${i}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePinChange(i, e.target.value)}
                        onKeyDown={(e) => handlePinKeyDown(i, e)}
                        className="w-14 h-14 text-center text-2xl font-bold rounded-xl border-2 border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-background"
                      />
                    ))}
                  </div>
                </div>
                <Button
                  data-testid="pin-submit-btn"
                  type="button"
                  className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-medium"
                  disabled={loading || pinDigits.some(d => !d)}
                  onClick={() => submitPin(pinDigits.join(""))}
                >
                  {loading ? "Signing in..." : "Sign In with PIN"}
                </Button>
              </div>
            )}

            <p className="text-center text-sm text-muted-foreground">
              {expectedRole === "student" 
                ? "Contact your teacher if you don't have login credentials"
                : expectedRole === "teacher"
                ? "Contact the admin if you don't have an account"
                : "Contact your academy admin or teacher if you don't have an account"
              }
            </p>
            
            {expectedRole && (
              <p className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => navigate(expectedRole === "student" ? "/login?role=teacher" : "/login?role=student")}
                  className="text-primary hover:underline"
                >
                  {expectedRole === "student" ? "Login as Teacher instead" : "Login as Student instead"}
                </button>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
